import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";
import { Shell } from "@/components/Shell";
import { InstallPrompt } from "@/components/InstallPrompt";
import { StreakBadge } from "@/components/StreakBadge";
import { api, errMsg, fmtDuration, fmtTime } from "@/lib/api";
import { useCountdown } from "@/hooks/useCountdown";

function Stat({ label, value, testId, accent }) {
  return (
    <div className="panel p-5" data-testid={testId}>
      <p className="eyebrow">{label}</p>
      <p className={`mt-2 font-mono text-3xl font-bold ${accent || "text-white"}`}>{value}</p>
    </div>
  );
}

export default function Result() {
  const [r, setR] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/result/today").then(({ data }) => setR(data)).catch((e) => setError(errMsg(e))); }, []);
  const untilNext = useCountdown(r?.next_day_opens_at, r?.server_now);

  if (error) return <Shell><p className="text-sm text-rose-300" data-testid="result-error">{error}</p></Shell>;
  if (!r) return <Shell><p className="eyebrow mt-10">Loading result…</p></Shell>;

  const lastDay = r.day >= r.total_days;
  return (
    <Shell width="wide">
      <div className="fade-up">
        <p className="eyebrow">Day {r.day} of {r.total_days} · {r.day_complete ? "Complete" : r.challenges_completed === 0 ? "Not started" : "In progress"}</p>
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight md:text-4xl" data-testid="result-title">{r.day_complete ? "Day done." : r.challenges_completed === 0 ? `Ready for Day ${r.day}` : "Today so far"}</h1>
        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <Stat label="Today's score" value={r.today_score} testId="result-today-score" accent="text-emerald-400" />
          <Stat label="Season total" value={r.season_score} testId="result-season-score" />
          <Stat label="Current rank" value={r.rank ? `#${r.rank}` : "—"} testId="result-rank" accent="text-amber-400" />
          <Stat label="Completed" value={`${r.challenges_completed}/${r.question_count}`} testId="result-completed" />
          <Stat label="Time today" value={fmtTime(r.today_response_ms)} testId="result-today-time" />
          <Stat label="Total time" value={fmtTime(r.season_response_ms)} testId="result-season-time" />
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
        <StreakBadge streak={r.streak} activeToday={r.streak_active_today} totalDays={r.total_days} completedDays={r.completed_days} />
        <div className="panel p-5" data-testid="result-next-countdown">
          <p className="eyebrow">{r.status === "ended" || (lastDay && r.day_complete) ? "Season finished" : "Next challenges unlock in"}</p>
          {r.next_day_opens_at ? (
            <p className="mt-2 font-mono text-3xl font-bold text-blue-400" data-testid="result-countdown-value">{untilNext != null ? fmtDuration(untilNext) : "—"}</p>
          ) : (
            <p className="mt-2 text-sm text-slate-400">Final standings are on the leaderboard. Thanks for playing the beta.</p>
          )}
        </div>
        </div>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <Link to="/leaderboard" className="btn-primary sm:min-w-[220px]" data-testid="result-leaderboard-button"><Trophy size={18} /> View leaderboard</Link>
          {!r.day_complete && r.status === "live" && <Link to="/play" className="btn-secondary" data-testid="result-continue-button">{r.challenges_completed === 0 ? "Start today's challenges" : "Continue today's challenges"}</Link>}
          <Link to="/" className="py-2 text-center text-sm text-slate-400 hover:text-white sm:px-3" data-testid="result-home-link">Back to home</Link>
        </div>
        {r.day_complete && <p className="mt-4 text-xs text-slate-500">Answers are never revealed. Your solving time is the leaderboard tiebreaker.</p>}
        <div className="max-w-md"><InstallPrompt /></div>
      </div>
    </Shell>
  );
}
